import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import MedalOutlineIcon from 'app/components/core/icons/MedalOutlineIcon';
import { useGamificationQuery } from 'app/pages/Gamification/hooks/useGamificationQuery';
import { getGamificationLevelPresentation } from 'app/helpers/gamification/getGamificationLevelPresentation';
import * as S from './styles';

function formatRankingLabel(ranking: unknown): string | null {
  if (ranking == null) return null;
  if (typeof ranking === 'number') return `${ranking}º`;
  if (typeof ranking === 'string') return ranking;
  if (typeof ranking === 'object' && ranking !== null && 'position' in ranking) {
    const p = (ranking as { position?: number }).position;
    if (typeof p === 'number') return `${p}º`;
  }
  return null;
}

const GamificationMenuLevelHeader: React.FC = () => {
  const { userStats, levels, loading } = useGamificationQuery();
  const presentation = useMemo(
    () => getGamificationLevelPresentation(userStats, levels),
    [userStats, levels],
  );

  const rankingLabel = formatRankingLabel(userStats.ranking);

  if (loading) {
    return (
      <S.HeaderBlock>
        <S.TopRow>
          <S.Left>
            <Skeleton variant="text" width={120} height={24} />
            <Skeleton variant="text" width={180} height={20} />
          </S.Left>
          <Skeleton variant="rounded" width={100} height={36} />
        </S.TopRow>
        <S.ProgressBar>
          <S.ProgressFill data-testid="progress-fill" pct={0} />
        </S.ProgressBar>
      </S.HeaderBlock>
    );
  }

  const { currentLevelConfig, currentLevelIndex } = presentation;
  const safeCurrentXP = Number(userStats.currentXP) || 0;
  const safeNextLevelXP = Number(userStats.nextLevelXP) || 100;
  const levelName = currentLevelConfig?.name || '—';
  const levelTitle = `Nível ${currentLevelIndex + 1}: ${levelName}`;

  const progressPct = Math.min(
    100,
    Math.round((safeCurrentXP / safeNextLevelXP) * 100),
  );

  return (
    <S.HeaderBlock>
      <S.TopRow>
        <S.Left>
          <S.LevelTitle>{levelTitle}</S.LevelTitle>
          <S.XpRow>
            <MedalOutlineIcon size={14} color="#017858" />
            <S.XpText>
              <strong>{safeCurrentXP} XP</strong>
              {` de ${safeNextLevelXP}`}
            </S.XpText>
          </S.XpRow>
        </S.Left>
        {rankingLabel ? (
          <S.RankingBadge>
            <S.RankingTrophySlot>
              <EmojiEventsOutlinedIcon sx={{ fontSize: 16 }} />
            </S.RankingTrophySlot>
            {rankingLabel}
          </S.RankingBadge>
        ) : null}
      </S.TopRow>
      <S.ProgressBar>
        <S.ProgressFill data-testid="progress-fill" pct={progressPct} />
      </S.ProgressBar>
    </S.HeaderBlock>
  );
};

export default GamificationMenuLevelHeader;
